import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { Text } from "components/Typography/text.styled";
import {
  NumberInputContainer,
  FeesContainer,
  RowWrapper,
  InfoCircle,
} from "./hourlyRate.styled";
import { InputContainer } from "components/form/inputs/inputs.styled";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { CompanyGigShift, GigCosts, GigType, PaymentMethod } from "model/Gig";
import { getCosts } from "web-apps/company/utils/utils";
import { TextInputWithLabel } from "../../../general/textInput/textInput.component";
import { SimpleRow } from "../../../organizationalStructure/organizationalStructure.styled";
import { DividerLine } from "../../gigApplicants/gigApplicants.styled";

type Props = {
  minHourlyRate: number;
  maxHourlyRate: number;
  onChange: (min: number, max: number) => void;
  hours: number;
  shifts: CompanyGigShift[];
  gigType: GigType | undefined;
  paymentType: PaymentMethod | undefined;
};

const emptyCosts = {
  grossSalary: 0,
  obAmount: 0,
  socialFee: 0,
  simflexityFee: 0,
  totalCost: 0,
  vacationPayAmount: 0
};

export const HourlyRate: React.FC<Props> = ({
  minHourlyRate,
  maxHourlyRate,
  onChange,
  hours,
  shifts,
  gigType,
  paymentType,
}) => {
  const { t } = useTranslation();
  const [showFeeInfo, setShowFeeInfo] = useState(false);
  const [companyState] = useCompanyStore();
  const [gigMinCost, setGigMinCost] = useState<GigCosts>(emptyCosts);
  const [gigMaxCost, setGigMaxCost] = useState<GigCosts>(emptyCosts);
  const totalDeduction = shifts.reduce(
    (sum, shift) =>
      sum + (shift.deductionAmount !== undefined ? shift.deductionAmount : 0),
    0
  );

  const validateHourlyRate = () => maxHourlyRate >= minHourlyRate;

  useEffect(() => {
    if (companyState.company?.id) {
      getCosts(
        shifts,
        minHourlyRate,
        maxHourlyRate,
        gigType,
        companyState.company.id,
        paymentType
      ).then((costs) => {
        setGigMinCost(costs.minCost);
        setGigMaxCost(costs.maxCost);
      });
    }
  }, [minHourlyRate, maxHourlyRate, shifts, paymentType]);

  return (
    <InputContainer style={{ marginTop: "2%" }}>
      <Text
        fontSize={FontSize.Standard}
        fontFamily={FontFamily.MontserratRegular}
        style={{ marginBottom: "1.5%" }}
      >
        {t("GigsCompany.SelectASpan")}
      </Text>
      <NumberInputContainer
        style={{
          marginLeft: 0,
          width: "100%",
          display: "flex",
          alignItems: "flex-end",
        }}
      >
        <div style={{ width: "20%", marginRight: "1%" }}>
          <TextInputWithLabel
            label={t("CreateShift.minRate")}
            value={minHourlyRate.toString()}
            onChange={(e: any) =>
              onChange(parseInt(e.currentTarget.value) || 0, maxHourlyRate)
            }
            type="number"
            inputSize="small"
          />
        </div>
        <Text
          color={Color.SeaBlue600}
          fontSize={FontSize.H4}
          style={{ margin: "0 1% 3%" }}
        >
          -
        </Text>
        <div style={{ width: "20%", marginLeft: "1%" }}>
          <TextInputWithLabel
            label={t("CreateShift.maxRate")}
            value={maxHourlyRate.toString()}
            onChange={(e: any) =>
              onChange(minHourlyRate, parseInt(e.currentTarget.value) || 0)
            }
            type="number"
            inputSize="small"
          />
        </div>
      </NumberInputContainer>

      {!validateHourlyRate() && (
        <Text fontSize={FontSize.Small} color={Color.Destructive}>
          {t("GigsCompany.MaxRateMustBeHigherThanMinRate")}
        </Text>
      )}

      <RowWrapper style={{ justifyContent: "flex-start", marginTop: "2%" }}>
        <Text
          fontFamily={FontFamily.MontserratRegular}
          style={{ marginBottom: 0 }}
        >
          {t("GigsCompany.EstimatedCost")}
        </Text>
        <InfoCircle
          onMouseEnter={() => setShowFeeInfo(true)}
          onMouseLeave={() => setShowFeeInfo(false)}
          style={{ marginBottom: 0, marginTop: "1%" }}
        >
          <Text
            fontFamily={FontFamily.LeagueSpartanBold}
            color={Color.White}
            padding
            fontSize={FontSize.Small}
            style={{ marginBottom: "0.2%" }}
          >
            i
          </Text>
        </InfoCircle>
      </RowWrapper>

      {showFeeInfo && (
        <Text fontSize={FontSize.Small}>{t("GigsCompany.FeeInfo")}</Text>
      )}
      <RowWrapper>
        <FeesContainer>
          {hours ? (
            <div style={{ display: "inline-block", width: "100%" }}>
              <SimpleRow style={{ marginTop: "1%" }}>
                <Text fontFamily={FontFamily.MontserratSemiBold}>
                  {t("GigsCompany.GrossSalary")}
                </Text>
                <Text>
                  {gigMinCost.grossSalary.toFixed(2)} -{" "}
                  {gigMaxCost.grossSalary.toFixed(2)} kr
                </Text>
              </SimpleRow>
              <DividerLine />

              <SimpleRow style={{ marginTop: "1%" }}>
                <Text fontFamily={FontFamily.MontserratSemiBold}>
                  {t("ProfileCompany.OB")}
                </Text>
                <Text>
                  {gigMinCost?.obAmount} - {gigMaxCost?.obAmount} kr
                </Text>
              </SimpleRow>

              <DividerLine style={{ width: "80%" }} />

              <SimpleRow style={{ marginTop: "1%" }}>
                <Text fontFamily={FontFamily.MontserratSemiBold}>
                  {t("GigsCompany.SocialFees")}
                </Text>
                <Text>
                  {(gigMinCost?.socialFee ? gigMinCost.socialFee : 0).toFixed(
                    2
                  )}
                  -
                  {(gigMaxCost?.socialFee ? gigMaxCost.socialFee : 0).toFixed(
                    2
                  )}
                  kr
                </Text>
              </SimpleRow>
              <DividerLine />

              {paymentType === PaymentMethod.SIMFLEXITY_PAY && (
                <>
                  <SimpleRow style={{ marginTop: "1%" }}>
                    <Text fontFamily={FontFamily.MontserratSemiBold}>
                      {t("GigsCompany.SimflexityFee")}
                    </Text>
                    <Text>
                      {gigMinCost?.simflexityFee} - {gigMaxCost?.simflexityFee}{" "}
                      kr
                    </Text>
                  </SimpleRow>
                  <DividerLine />
                </>
              )}

              <SimpleRow style={{ marginTop: "1%" }}>
                <Text fontFamily={FontFamily.MontserratSemiBold}>
                  {t("GigsCompany.VacationPayAmount")}
                </Text>
                <Text>
                  {gigMinCost?.vacationPayAmount} - {gigMaxCost?.vacationPayAmount} kr
                </Text>
              </SimpleRow>
              <DividerLine />

              {totalDeduction !== 0 && totalDeduction !== undefined && (
                <>
                  <SimpleRow style={{ marginTop: "1%" }}>
                    <Text fontFamily={FontFamily.MontserratSemiBold}>
                      {t("GigsCompany.Deduction")}:
                    </Text>
                    <Text>{totalDeduction + " kr"}</Text>
                  </SimpleRow>
                  <DividerLine />
                </>
              )}

              <SimpleRow style={{ marginTop: "3%" }}>
                <Text fontFamily={FontFamily.MontserratSemiBold}>
                  {t("GigsCompany.TotalCost")}
                </Text>
                <Text>
                  {gigMinCost?.totalCost.toFixed(2)} -{" "}
                  {gigMaxCost?.totalCost.toFixed(2)} kr
                </Text>
              </SimpleRow>

              <Text
                color={Color.SeaBlue600}
                fontSize={FontSize.Small}
                style={{ width: "40%", lineHeight: "120%" }}
              >
                {t("GigsCompany.FinalFee")}
              </Text>
            </div>
          ) : (
            <Text color={Color.SeaBlue600}>
              {t("GigsCompany.AddShiftToSeeEstimatedCost")}
            </Text>
          )}
        </FeesContainer>
      </RowWrapper>
    </InputContainer>
  );
};
